/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeButton,
  DimeCalendar,
  DimeCard,
  DimeCardContent,
  DimeCardFooter,
  DimeSeparator,
  formatLocalDate,
  type DateRange,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/calendar')({
  component: CalendarPage,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-4 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

// ── Examples ───────────────────────────────────────────────────────────────────

function BasicExample() {
  const [date, setDate] = React.useState<Date | undefined>();
  return (
    <div className="flex flex-col gap-2">
      <DimeCalendar
        mode="single"
        selected={date}
        onSelect={setDate}
        className="rounded-lg border"
      />
      <p className="text-sm text-muted-foreground">
        Selected: {date ? formatLocalDate(date) : 'none'}
      </p>
    </div>
  );
}

function RangeExample() {
  const [range, setRange] = React.useState<DateRange | undefined>();
  return (
    <div className="flex flex-col gap-2">
      <DimeCalendar
        mode="range"
        selected={range}
        onSelect={setRange}
        numberOfMonths={2}
        className="rounded-lg border"
      />
      <p className="text-sm text-muted-foreground">
        From: {range?.from ? formatLocalDate(range.from) : '—'}&nbsp; To:{' '}
        {range?.to ? formatLocalDate(range.to) : '—'}
      </p>
    </div>
  );
}

function MultipleExample() {
  const [dates, setDates] = React.useState<Date[] | undefined>();
  return (
    <div className="flex flex-col gap-2">
      <DimeCalendar
        mode="multiple"
        selected={dates}
        onSelect={setDates}
        className="rounded-lg border"
      />
      <p className="text-sm text-muted-foreground">Selected: {dates?.length ?? 0} day(s)</p>
    </div>
  );
}

function WeekNumbersExample() {
  const [date, setDate] = React.useState<Date | undefined>();
  return (
    <DimeCalendar
      mode="single"
      selected={date}
      onSelect={setDate}
      showWeekNumber
      className="rounded-lg border"
    />
  );
}

const PRESETS = [
  { label: 'Today', days: 0 },
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'In a week', days: 7 },
  { label: 'In 2 weeks', days: 14 },
];

function addDays(date: Date, days: number): Date {
  const result = new Date(date.getTime());
  result.setDate(result.getDate() + days);
  return result;
}

function PresetsExample() {
  const today = new Date();
  const [date, setDate] = React.useState<Date | undefined>(undefined);
  const [month, setMonth] = React.useState<Date>(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );

  return (
    <DimeCard size="sm" className="w-fit max-w-[300px]">
      <DimeCardContent>
        <DimeCalendar
          mode="single"
          selected={date}
          onSelect={setDate}
          month={month}
          onMonthChange={setMonth}
          fixedWeeks
          className="p-0 [--cell-size:--spacing(9.5)]"
        />
      </DimeCardContent>
      <DimeCardFooter className="flex flex-wrap gap-2 border-t">
        {PRESETS.map((preset) => (
          <DimeButton
            key={preset.days}
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => {
              const next = addDays(new Date(), preset.days);
              setDate(next);
              setMonth(new Date(next.getFullYear(), next.getMonth(), 1));
            }}
          >
            {preset.label}
          </DimeButton>
        ))}
      </DimeCardFooter>
    </DimeCard>
  );
}

function BookedDatesExample() {
  const today = new Date();
  const booked = [
    addDays(today, 2),
    addDays(today, 3),
    addDays(today, 8),
    addDays(today, 9),
    addDays(today, 10),
  ];
  const [date, setDate] = React.useState<Date | undefined>(undefined);

  return (
    <DimeCalendar
      mode="single"
      selected={date}
      onSelect={setDate}
      disabled={booked}
      modifiers={{ booked }}
      modifiersClassNames={{ booked: 'line-through opacity-40' }}
      className="rounded-lg border"
    />
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

function CalendarPage() {
  return (
    <div className="max-w-4xl">
      <h1 className="mb-1 text-3xl font-bold">Calendar</h1>
      <p className="mb-8 text-muted-foreground">
        An inline date picker built on{' '}
        <a
          href="https://react-day-picker.js.org"
          className="underline"
          target="_blank"
          rel="noreferrer"
        >
          React DayPicker
        </a>
        . All React DayPicker props are passed through directly.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <BasicExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">mode="single"</code>,{' '}
          <code className="font-mono">selected</code>, and{' '}
          <code className="font-mono">onSelect</code>. Add{' '}
          <code className="font-mono">className="rounded-lg border"</code> for the standard bordered
          appearance.
        </p>
      </DocsSection>

      <DocsSection title="Range">
        <Preview>
          <RangeExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">mode="range"</code> with a{' '}
          <code className="font-mono">{'{ from?: Date; to?: Date }'}</code> state. Pass{' '}
          <code className="font-mono">numberOfMonths={2}</code> to show two months side by side.
        </p>
      </DocsSection>

      <DocsSection title="Multiple">
        <Preview>
          <MultipleExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">mode="multiple"</code> with a{' '}
          <code className="font-mono">Date[]</code> state to allow selecting individual
          non-contiguous dates.
        </p>
      </DocsSection>

      <DocsSection title="Month and year selector">
        <Preview>
          <DimeCalendar mode="single" captionLayout="dropdown" className="rounded-lg border" />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">captionLayout="dropdown"</code> to replace the prev/next
          arrow navigation with month and year dropdowns.
        </p>
      </DocsSection>

      <DocsSection title="Week numbers">
        <Preview>
          <WeekNumbersExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">showWeekNumber</code> to display ISO week numbers in a
          column on the left side of the calendar.
        </p>
      </DocsSection>

      <DocsSection title="Presets">
        <Preview>
          <PresetsExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Compose <code className="font-mono">DimeCalendar</code> inside a{' '}
          <code className="font-mono">DimeCard</code> with preset{' '}
          <code className="font-mono">DimeButton</code>s in the footer. Use{' '}
          <code className="font-mono">month</code> +{' '}
          <code className="font-mono">onMonthChange</code> to programmatically navigate to the
          selected date's month, and <code className="font-mono">fixedWeeks</code> to keep the
          calendar height stable.
        </p>
      </DocsSection>

      <DocsSection title="Booked dates">
        <Preview>
          <BookedDatesExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass an array of <code className="font-mono">Date</code>s to{' '}
          <code className="font-mono">disabled</code> to block selection. Use{' '}
          <code className="font-mono">modifiers</code> +{' '}
          <code className="font-mono">modifiersClassNames</code> to apply custom styling to those
          same dates (here: strikethrough).
        </p>
      </DocsSection>

      <DocsSection title="Disabled">
        <Preview>
          <DimeCalendar mode="single" disabled className="rounded-lg border" />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">disabled</code> to prevent all interaction. You can also
          pass a <code className="font-mono">Matcher</code> (e.g. a date, range, or function) to
          disable specific days.
        </p>
      </DocsSection>

      <DocsSection title="Hide outside days">
        <Preview>
          <DimeCalendar mode="single" showOutsideDays={false} className="rounded-lg border" />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">showOutsideDays={'{false}'}</code> to hide days that fall
          outside the current month. Defaults to <code className="font-mono">true</code>.
        </p>
      </DocsSection>
    </div>
  );
}
